import { useCallback, useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { DeletableEntity, DeleteOutcome, RecordDeletionGateway } from '@forq/contracts';
import { DeleteDialog, useDeleteDialog } from './DeleteDialog';

export interface RecordDeleteButtonProps {
  readonly gateway: RecordDeletionGateway;
  readonly tenantId: string;
  readonly entity: DeletableEntity;
  readonly id: string;
  /** Wie der Datensatz heisst: "Kunde", "Maschine", "Vorgang". */
  readonly noun: string;
  /**
   * Name des Datensatzes aus Sicht des Moduls. Rückfallebene, falls die Vorschau keinen liefert --
   * verbindlich ist der Name aus der Datenbank, denn der beschreibt, was wirklich gelöscht wird.
   */
  readonly label?: string;
  readonly canDelete: boolean;
  /** Danach: Liste neu laden und Auswahl aufheben. Bekommt mit, WAS passiert ist. */
  readonly onDone: (outcome: DeleteOutcome) => void;
  readonly className?: string;
}

/**
 * Knopf plus Bestätigungsdialog in einem -- damit jedes Modul EINE Zeile braucht statt der
 * immergleichen fünf Zustandsvariablen. Sechs Nachbauten wären sechs Gelegenheiten, das
 * Neuladen nach dem Löschen zu vergessen und eine Geisterzeile stehen zu lassen.
 */
export function RecordDeleteButton({
  gateway,
  tenantId,
  entity,
  id,
  noun,
  label,
  canDelete,
  onDone,
  className,
}: RecordDeleteButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={className}
        disabled={!canDelete}
        onClick={() => setOpen(true)}
        title={canDelete ? undefined : 'Dazu fehlt dir die Berechtigung.'}
      >
        <Trash2 size={14} /> Löschen
      </button>
      {open && (
        // key erzwingt einen frischen Dialog je Datensatz: sonst zeigt er beim zweiten Öffnen
        // noch den Plan des vorigen.
        <DeleteFlow
          key={`${entity}:${id}`}
          gateway={gateway}
          tenantId={tenantId}
          entity={entity}
          id={id}
          noun={noun}
          label={label}
          onClose={() => setOpen(false)}
          onDone={(outcome) => { setOpen(false); onDone(outcome); }}
        />
      )}
    </>
  );
}

export interface RecordDeleteRequest {
  readonly entity: DeletableEntity;
  readonly id: string;
  /** Wie der Datensatz heisst: "Kunde", "Maschine", "Vorgang". */
  readonly noun: string;
  readonly label?: string;
  readonly onDone: (outcome: DeleteOutcome) => void;
}

/**
 * Löschdialog auf Seitenebene -- für Auslöser, die kein eigener Knopf sind (z. B. das
 * Rechtsklick-Kontextmenü). `requestDelete(...)` öffnet die Vorschau, `deleteDialog` wird einmal im
 * Seitenbaum gerendert. Gleiche Vorschau-/Bestätigungslogik wie der RecordDeleteButton.
 */
export function useRecordDeleteFlow(gateway: RecordDeletionGateway, tenantId: string) {
  const [request, setRequest] = useState<RecordDeleteRequest | null>(null);
  const requestDelete = useCallback((next: RecordDeleteRequest) => setRequest(next), []);
  const close = useCallback(() => setRequest(null), []);
  const deleteDialog = request ? (
    <DeleteFlow
      key={`${request.entity}:${request.id}`}
      gateway={gateway}
      tenantId={tenantId}
      entity={request.entity}
      id={request.id}
      noun={request.noun}
      label={request.label}
      onClose={close}
      onDone={(outcome) => { close(); request.onDone(outcome); }}
    />
  ) : null;
  return { requestDelete, deleteDialog };
}

function DeleteFlow({
  gateway,
  tenantId,
  entity,
  id,
  noun,
  label,
  onClose,
  onDone,
}: {
  readonly gateway: RecordDeletionGateway;
  readonly tenantId: string;
  readonly entity: DeletableEntity;
  readonly id: string;
  readonly noun: string;
  readonly label?: string;
  readonly onClose: () => void;
  readonly onDone: (outcome: DeleteOutcome) => void;
}) {
  const loadPlan = useCallback(
    () => gateway.planDeletion(tenantId, entity, id),
    [entity, gateway, id, tenantId],
  );
  const run = useCallback(
    async () => { onDone(await gateway.deleteRecord(tenantId, entity, id)); },
    [entity, gateway, id, onDone, tenantId],
  );
  const { plan, busy, error, confirm } = useDeleteDialog(loadPlan, run);

  return (
    <DeleteDialog
      plan={plan}
      noun={noun}
      fallbackLabel={label}
      busy={busy}
      error={error}
      onConfirm={confirm}
      onCancel={onClose}
    />
  );
}

/**
 * Die Meldung nach dem Löschen -- sagt ausdrücklich, wenn es anders kam als angekündigt.
 * `fallbackLabel` springt ein, wenn die Datenbank keinen Namen zurückgibt: "Kunde  wurde
 * gelöscht" mit Loch in der Mitte liest sich wie ein Fehler.
 */
export function describeDeleteOutcome(outcome: DeleteOutcome, noun: string, fallbackLabel?: string): string {
  const name = outcome.label || fallbackLabel || '';
  const betreff = name ? `${noun} ${name}` : noun;
  if (outcome.action === 'deleted') return `${betreff} wurde gelöscht.`;
  const woran = (outcome.references ?? [])
    .map((reference) => `${reference.count} ${reference.label}`)
    .join(', ');
  const grund = woran ? ` – daran hängen noch ${woran}.` : '.';
  return outcome.unexpected
    ? `${betreff} wurde archiviert statt gelöscht${grund}`
    : `${betreff} wurde archiviert${grund}`;
}
